import { List as ImList, Map as MapImmutable } from 'immutable';
import { DateTime } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector } from 'reselect';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { tagsSelectors } from 'app-components/flux/tags';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import { SCHEDULED_TRANSACTIONS_REDUCER_KEY, STTypeEnum } from './scheduledTransactionsTypes';
import {
  frequencyAliasFromRecurrence,
  isIncomeSeriesFromModelOrTxn,
  isIncomeSeriesFromTxn,
  type ScheduledSeriesClassificationModel,
  seriesMatchesTypeFilter,
  typeFromScheduledTransaction,
} from './scheduledTransactionsUtils';

type State = Pick<RootState, typeof SCHEDULED_TRANSACTIONS_REDUCER_KEY>;

const payeeCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const getScheduledTransactionsStore = (state: State) => state[SCHEDULED_TRANSACTIONS_REDUCER_KEY];

export const getResourcesById = (state: State) => getScheduledTransactionsStore(state).resourcesById;
export const getLoadPending = (state: State) => getScheduledTransactionsStore(state).loadPending;
export const getError = (state: State) => getScheduledTransactionsStore(state).error;
export const getIsLoading = (state: State) => getScheduledTransactionsStore(state).isLoading;
export const getLastSyncDate = (state: State) => getScheduledTransactionsStore(state).lastSyncDate;

export const getScheduledTransactions = getResourcesById;
export const getScheduledTransactionById = (state: State, id: string) => getScheduledTransactions(state).get(id);
export const getScheduledTransactionByClientId = (state: State, clientId: string) =>
  getScheduledTransactions(state).find((scheduledTransaction) => scheduledTransaction.clientId === clientId);

export const getScheduledTransactionsSortedByPayee = createSelector(
  getScheduledTransactions,
  (scheduledTransactions) =>
    !scheduledTransactions
      ? null
      : scheduledTransactions.sort((a, b) =>
          payeeCollator.compare(a.transaction?.payee ?? '', b.transaction?.payee ?? ''),
        ),
);

export const getUserVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById) =>
    scheduledTransactionsById.filter((scheduledTransaction) => scheduledTransaction.isUserVerified),
);

export const hasUserVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById): boolean =>
    scheduledTransactionsById.some((scheduledTransaction) => Boolean(scheduledTransaction.isUserVerified)),
);

export const getNonVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById) =>
    scheduledTransactionsById.filter((scheduledTransaction) => !scheduledTransaction.isUserVerified),
);

export const existsScheduledTransactionWithTag = createSelector(
  getUserVerifiedScheduledTransactions,
  (_: unknown, { id }: { id?: string }) => id,
  (state, tagId) =>
    tagId
      ? Boolean(
          state.find((stxn) =>
            stxn.transaction ? Boolean(transactionsUtils.txnHasTag(stxn.transaction, tagId)) : false,
          ),
        )
      : false,
);

export const getBillsAndIncome = createSelector(
  getScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) => ({
    bills: scheduledTransactions
      .filter((model) => !isIncomeSeriesFromTxn(model.transaction, incomeCOAs))
      .toOrderedSet(),
    income: scheduledTransactions
      .filter((model) => isIncomeSeriesFromTxn(model.transaction, incomeCOAs))
      .toOrderedSet(),
  }),
);
export const getBillsAndIncomeUserVerified = createSelector(
  getUserVerifiedScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) =>
    MapImmutable({
      bills: scheduledTransactions
        .filter((model) => !isIncomeSeriesFromTxn(model.transaction, incomeCOAs))
        .toOrderedSet(),
      income: scheduledTransactions
        .filter((model) => isIncomeSeriesFromTxn(model.transaction, incomeCOAs))
        .toOrderedSet(),
    }),
);
export const getBillsAndIncomeNonUserVerified = createSelector(
  getNonVerifiedScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) => ({
    bills: scheduledTransactions
      .filter((model) => !isIncomeSeriesFromTxn(model.transaction, incomeCOAs))
      .toOrderedSet(),
    income: scheduledTransactions
      .filter((model) => isIncomeSeriesFromTxn(model.transaction, incomeCOAs))
      .toOrderedSet(),
  }),
);

export const getAccountFilteredBillsAndIncome = createSelector(
  getScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (_state: unknown, accountIds: string[]) => accountIds, // array of accountIds to filter by
  (scheduledTransactions, incomeCOAs, accountIds) => {
    const accountSchedules = scheduledTransactions.filter((model) =>
      accountIds.includes(model.transaction?.accountId ?? ''),
    );
    return {
      bills: accountSchedules.filter((model) => !isIncomeSeriesFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
      income: accountSchedules.filter((model) => isIncomeSeriesFromTxn(model.transaction, incomeCOAs)).toOrderedSet(),
    };
  },
);

export const getUnconnectedBills = createSelector(
  getUserVerifiedScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactionsById, incomeCOAs) =>
    scheduledTransactionsById.filter(
      (model) =>
        (!model.billPresentmentAccountId || model.billPresentmentAccountId === '0') &&
        !incomeCOAs.has(model?.transaction?.coa?.id ?? ''),
    ),
);

export const modelTypes = Object.freeze({
  ALL: 'All types',
  INCOME: 'Income',
  BILLS: 'Bills',
  TRANSFERS: 'Transfers',
});

export const typeFilteredScheduledTransactions = createSelector(
  getUserVerifiedScheduledTransactions,
  (_state: unknown, type: string) => type,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, type, incomeCOAIds) => {
    let filteredSchedules = scheduledTransactions;
    if (type) {
      // filter stuff
      if (type === modelTypes.INCOME) {
        // only show standard income
        filteredSchedules = scheduledTransactions.filter((model) =>
          isIncomeSeriesFromTxn(model?.transaction, incomeCOAIds),
        );
      } else if (type === modelTypes.TRANSFERS) {
        // match the Bills & Income Transfers tab (transfers and credit card payments)
        filteredSchedules = scheduledTransactions.filter((model) =>
          seriesMatchesTypeFilter(typeFromScheduledTransaction(model), STTypeEnum.TRANSFER),
        );
      } else if (type === modelTypes.BILLS) {
        // don't show income
        filteredSchedules = scheduledTransactions.filter(
          (model) => !isIncomeSeriesFromTxn(model?.transaction, incomeCOAIds),
        );
      } else {
        // show everything
        filteredSchedules = scheduledTransactions;
      }
    }
    return filteredSchedules;
  },
);

export const isIncomeSeriesTxn = createSelector(
  (_state: unknown, txn: TransactionProps | null | undefined) => txn,
  categoriesSelectors.getIncomeCOAIds,
  isIncomeSeriesFromTxn,
);

export const isIncomeSeries = createSelector(
  (_state: unknown, model: ScheduledSeriesClassificationModel | null | undefined) => model,
  (_state: unknown, _model: ScheduledSeriesClassificationModel | null | undefined, txn?: TransactionProps | null) =>
    txn,
  categoriesSelectors.getIncomeCOAIds,
  isIncomeSeriesFromModelOrTxn,
);

export const bucketizeTransactionsByAccountId = createCachedSelector(
  (transactionsById: MapImmutable<string, { accountId: string }>) => transactionsById,
  (transactionsById) =>
    MapImmutable<string, ImList<{ accountId: string }>>().withMutations(
      (transactionsByAccountIdMutable) =>
        transactionsById &&
        transactionsById.forEach((transaction) =>
          transactionsByAccountIdMutable.set(
            transaction.accountId,
            (transactionsByAccountIdMutable.get(transaction.accountId) || ImList()).push(transaction),
          ),
        ),
    ),
)({
  keySelector: (transactionsById) => transactionsById?.hashCode() ?? 'none',
  cacheObject: new LruObjectCache({ cacheSize: 6 }),
});

// Used in all recurring search for better keyword mapping.
export const schTxnsSearchNames = createSelector(
  getScheduledTransactions,
  accountsSelectors.getAccountsById,
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (schTxns, accountsById, categoriesById, tagsById) =>
    schTxns.map((schTxn) => ({
      payeeName: schTxn.transaction?.payee || 'New Series',
      amount: schTxn.transaction?.amount != null ? String(schTxn.transaction.amount) : '',
      accountName: accountsById?.get(schTxn.transaction?.accountId ?? '')?.name || '',
      frequency: frequencyAliasFromRecurrence(schTxn.recurrence) || '',
      nextDate: (schTxn.dueOn && DateTime.fromISO(schTxn.dueOn)?.toLocaleString()) || '',
      type: schTxn.type || '',
      category: categoriesById?.get(schTxn.transaction?.coa?.id ?? '')?.name || '',
      tags: schTxn.transaction?.tags?.map((tag) => tagsById?.get(tag?.id || '')?.name)?.join?.(' ') || '',
    })),
);

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(bucketizeTransactionsByAccountId);
